import Cookies from 'js-cookie';
import { isLightBootPublicPath } from '../utils/marketingPages.js';

const IS_PROD = process.env.NODE_ENV === 'production';
const ALLOW_IN_DEV =
  String(process.env.VUE_APP_LINKEDIN_INSIGHT_ALLOW_DEV ?? '').toLowerCase() ===
  'true';
const ENABLED =
  (IS_PROD || ALLOW_IN_DEV) &&
  String(process.env.VUE_APP_LINKEDIN_INSIGHT_ENABLED ?? 'true').toLowerCase() !==
    'false';
const PARTNER_ID = String(
  process.env.VUE_APP_LINKEDIN_PARTNER_ID || '9749209'
).trim();
const SCRIPT_ID = 'linkedin-insight-script';

function parseConversionId(value) {
  const parsed = Number.parseInt(String(value || '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

const PAGE_VIEW_CONVERSION_ID = parseConversionId(
  process.env.VUE_APP_LINKEDIN_PAGEVIEW_CONVERSION_ID
);

let initialized = false;
let lastPageViewSignature = null;

function normalizePath(path = '') {
  const source =
    path ||
    (typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/');
  try {
    return new URL(String(source), 'https://studio.mydid.local').pathname;
  } catch (_err) {
    return String(source || '/').split('?')[0] || '/';
  }
}

function getConsentCookieRaw() {
  if (typeof document === 'undefined') return null;
  let value = Cookies.get('consent');
  if (value !== undefined && value !== null && value !== '') return String(value);
  const match = document.cookie.match(/(?:^|;\s*)consent=([^;]*)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1].trim());
  } catch (_err) {
    return match[1];
  }
}

function isConsentGranted() {
  const consent = getConsentCookieRaw();
  if (consent == null || consent === '') return false;
  const normalized = String(consent).trim().toLowerCase();
  return (
    normalized === '1' ||
    normalized === 'true' ||
    normalized === 'yes' ||
    normalized === 'on'
  );
}

function ensureLinkedInQueue() {
  if (typeof window === 'undefined') return false;

  window._linkedin_partner_id = PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  if (!window._linkedin_data_partner_ids.includes(PARTNER_ID)) {
    window._linkedin_data_partner_ids.push(PARTNER_ID);
  }

  if (!window.lintrk) {
    window.lintrk = function lintrkProxy(action, payload) {
      window.lintrk.q.push([action, payload]);
    };
    window.lintrk.q = [];
  }
  return true;
}

function ensureLinkedInScript() {
  if (typeof document === 'undefined') return false;

  const existing = document.getElementById(SCRIPT_ID);
  if (existing) return true;

  const anchor = document.getElementsByTagName('script')[0];
  if (!anchor || !anchor.parentNode) return false;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  anchor.parentNode.insertBefore(script, anchor);
  return true;
}

function doLinkedInInsightInit(options = {}) {
  if (!ENABLED || !PARTNER_ID) return false;
  if (
    !options.allowPublicLightBootPath &&
    isLightBootPublicPath(normalizePath(options.path))
  ) {
    return false;
  }

  const queueOk = ensureLinkedInQueue();
  const scriptOk = ensureLinkedInScript();
  initialized = queueOk && scriptOk;
  return initialized;
}

export function initLinkedInInsight() {
  return doLinkedInInsightInit({ allowPublicLightBootPath: true });
}

export function applyLinkedInInsightConsent(accepted) {
  if (!ENABLED || !PARTNER_ID) return;
  if (accepted) {
    doLinkedInInsightInit();
  }
}

export function trackLinkedInInsightConversion(conversionId, extraPayload = {}) {
  if (!ENABLED || !PARTNER_ID) return false;
  const parsedConversionId = parseConversionId(conversionId);
  if (!parsedConversionId) return false;
  if (!initialized) doLinkedInInsightInit({ allowPublicLightBootPath: true });
  if (typeof window === 'undefined' || typeof window.lintrk !== 'function') {
    return false;
  }

  window.lintrk('track', {
    conversion_id: parsedConversionId,
    ...(extraPayload || {}),
  });
  return true;
}

export function trackLinkedInInsightPageView(path = null) {
  if (!PAGE_VIEW_CONVERSION_ID) return false;
  const signature = `${PAGE_VIEW_CONVERSION_ID}|${String(path || '').trim()}`;
  if (signature === lastPageViewSignature) return false;
  const sent = trackLinkedInInsightConversion(PAGE_VIEW_CONVERSION_ID, {
    path: path || null,
  });
  if (sent) {
    lastPageViewSignature = signature;
  }
  return sent;
}
